import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Video, Link as LinkIcon, Plus, Trash2, Megaphone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo da SUA como avatar dos comunicados
const COMMUNICATION_AVATAR_URL = '/logo.png';

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function AnnouncementsSection({ isAdmin, onAddClick, fullPage = false }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`, getAuthHeaders());
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Erro ao carregar comunicados');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Tem certeza que deseja deletar este comunicado?')) return;
    
    try {
      await axios.delete(`${API}/announcements/${announcementId}`, getAuthHeaders());
      toast.success('Comunicado deletado com sucesso');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erro ao deletar comunicado');
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await axios.put(`${API}/announcements/${announcement.id}`, 
        { active: !announcement.active }, 
        getAuthHeaders()
      );
      toast.success(announcement.active ? 'Comunicado desativado' : 'Comunicado ativado');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erro ao atualizar comunicado');
    }
  };

  // Para parceiros: se não há comunicados, não mostrar nada
  if (!isAdmin && announcements.length === 0) return null;

  // Para admin: sempre mostrar a seção de gerenciamento
  if (isAdmin) {
    return (
      <Card className="mb-6 border-border" data-testid="announcements-admin-section">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Megaphone className="w-6 h-6 text-primary" />
                Área Visual - Comunicados
              </CardTitle>
              <CardDescription>Gerencie os comunicados para os parceiros</CardDescription>
            </div>
            {/* Botão só aparece se NÃO estiver em fullPage (pois na página já tem um botão no header) */}
            {!fullPage && (
              <Button
                onClick={onAddClick}
                className="btn-transition"
                data-testid="add-announcement-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Comunicado
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-announcements-message">
              Nenhum comunicado publicado ainda
            </div>
          ) : (
            <div className="space-y-3" data-testid="announcements-list">
              {announcements.map((announcement, index) => (
                <div
                  key={announcement.id}
                  className={`border border-border rounded-lg p-4 bg-card ${!announcement.active ? 'opacity-60' : ''}`}
                  data-testid={`announcement-item-${index}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {/* Avatar com logo da SUA */}
                      <img
                        src={COMMUNICATION_AVATAR_URL}
                        alt="SUA Gestão"
                        className="w-9 h-9 rounded-full object-contain bg-white border border-border p-1"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div>
                        <h3 className="font-semibold">{announcement.title}</h3>
                        <span className="text-xs text-muted-foreground">
                          {announcement.type === 'flyer' && 'Flyer/Texto'}
                          {announcement.type === 'video' && 'Vídeo'}
                          {announcement.type === 'link' && 'Link'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={announcement.active ? 'default' : 'secondary'}>
                        {announcement.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(announcement)}
                        data-testid={`toggle-announcement-${index}`}
                      >
                        {announcement.active ? 'Desativar' : 'Ativar'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(announcement.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-announcement-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {announcement.content && (
                    <p className="text-sm text-muted-foreground mb-2">{announcement.content}</p>
                  )}
                  {/* Flyer Image - Show if URL starts with /images/ regardless of type */}
                  {announcement.url && announcement.url.startsWith('/images/') && (
                    <div className="mt-3 rounded-lg overflow-hidden">
                      <img 
                        src={announcement.url} 
                        alt={announcement.title}
                        className="w-full max-h-[400px] object-contain bg-muted/30"
                        data-testid={`flyer-image-${index}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  {/* Video Player */}
                  {announcement.type === 'video' && announcement.url && announcement.url.startsWith('/videos/') ? (
                    <div className="mt-3 rounded-lg overflow-hidden bg-black">
                      <video 
                        controls 
                        className="w-full max-h-[300px]"
                        data-testid={`video-player-${index}`}
                      >
                        <source src={announcement.url} type="video/mp4" />
                        Seu navegador não suporta vídeos.
                      </video>
                    </div>
                  ) : announcement.url && !announcement.url.startsWith('/images/') && (
                    <a
                      href={announcement.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {announcement.type === 'video' ? 'Assistir vídeo' : 'Abrir link'}
                      <LinkIcon className="w-3 h-3" />
                    </a>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Publicado em: {new Date(announcement.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Para parceiros: visualização simples dos comunicados ativos
  return (
    <Card className="mb-6 border-border" data-testid="announcements-partner-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-primary" />
          Avisos e Comunicados
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {announcements.map((announcement, index) => (
            <div
              key={announcement.id}
              className="border border-border rounded-lg p-4 bg-card"
              data-testid={`partner-announcement-${index}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  {/* Avatar com logo da SUA */}
                  <img
                    src={COMMUNICATION_AVATAR_URL}
                    alt="SUA Gestão"
                    className="w-9 h-9 rounded-full object-contain bg-white border border-border p-1"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <h3 className="font-semibold">{announcement.title}</h3>
                    <span className="text-xs text-muted-foreground">
                      {announcement.type === 'flyer' && 'Flyer/Texto'}
                      {announcement.type === 'video' && 'Vídeo'}
                      {announcement.type === 'link' && 'Link'}
                    </span>
                  </div>
                </div>
                <Badge variant="default">Novo</Badge>
              </div>
              {announcement.content && (
                <p className="text-sm text-muted-foreground mb-3">{announcement.content}</p>
              )}
              {/* Flyer Image - Show if URL starts with /images/ regardless of type */}
              {announcement.url && announcement.url.startsWith('/images/') && (
                <div className="mt-3 rounded-lg overflow-hidden">
                  <img 
                    src={announcement.url} 
                    alt={announcement.title}
                    className="w-full max-h-[400px] object-contain bg-muted/30"
                    data-testid={`partner-flyer-image-${index}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {announcement.type === 'video' && announcement.url && announcement.url.startsWith('/videos/') ? (
                <div className="mt-3 rounded-lg overflow-hidden bg-black">
                  <video 
                    controls 
                    className="w-full max-h-[300px]"
                    data-testid={`partner-video-player-${index}`}
                  >
                    <source src={announcement.url} type="video/mp4" />
                    Seu navegador não suporta vídeos.
                  </video>
                </div>
              ) : announcement.url && !announcement.url.startsWith('/images/') && (
                <a
                  href={announcement.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  {announcement.type === 'video' ? 'Assistir vídeo' : 'Abrir link'}
                  <LinkIcon className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
